import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { isHomeFromPath, isLuxuryHomesFromPath, isAnyResultsFromPath, RootState, VACATIONS_PAGE } from '@share/utils';
import { MarkDownEnum, UserAccountEnum } from '@common-types';
import { ItemTypeEnum } from '@share/common-types';
import {
  ChangePassword,
  CondoWeeksWrapper,
  AccountHubDashboardGate,
  ErrorPage,
  ErrorPanel,
  ForgotPassword,
  GenericNotFoundPage,
  IframeTours,
  IframeBackoffice,
  Markdown,
  PrivateCertificateRoute,
  PrivateRoute,
  PublicClubSiteLandingRedirect,
  PublicClubSiteSectionGuard,
  ResetPassword,
  SavedProperty,
  SendVerification,
  SignIn,
  SignUp,
  VerifyUser,
} from '@components';
import { CondoWorldMap } from '@share/components';
import { refreshAccountTemplate, refreshProfile } from '@share/store/slices';
import { IPageType, Routes } from '@share/constants';
import { NULL_VALUE } from '@constants';

import { SearchPage } from '../search';
import { MemberVideosPage } from '../member-videos';
import { MemberStoryPage } from '../member-story';
import { CondoPage } from '../condo';
import { PersonalInfoPage } from '../personal-info';
import { PromotionDetailsPage } from '../promotion-details';
import { BrioRciHowToPage } from '../brio-rci-how-to';
import { EliteRciHowToPage } from '../elite-rci-how-to';
import { WebinarsPage } from '../webinars';
import { ReservationsPage } from '../reservations';
import { RewardsPage } from '../rewards';
import { RewardsLearnPage } from '../rewards-learn';
import { RewardsQaPage } from '../rewards-qa';
import { AccountPage } from '../account';
import { NewMembersPage } from '../new-members';
import { ExperiencesPage } from '../experiences';
import { SidekickPage } from '../sidekicks';
import { SidekickTermsPage } from '../sidekicks-terms';
import { SidekickPrivacyPage } from '../sidekicks-privacy';
import { ThreeDS } from '../three-ds';
import { CarsPage } from '../cars';
import { CampingsPage } from '../campings';
import { CampingsWelcomePage } from '../campings-welcome';
import { CampingsFAQsPage } from '../campings-faqs';
import { CampingsBookingHowToPage } from '../campings-booking-how-to';

import { HotelDetailPage } from '../hotel-details';
import { GetawayPage } from '../getaway';
import { GetawayListPage } from '../getaway-list';
import { CondoDetailPage } from '../condo-details';
import { SubmitRequestPage } from '../all-inclusives/submit-request';
import { GetawayDetailsPage } from '../getaway-details';
import { CondoReviewBookPage } from '../condo-review-book';
import { ReservationCancellation } from '../reservation-cancellation';
import { HomePage } from '../home-page';
import { HomesPage } from '../homes';
import { Condominiums } from '../../components/condo/condominiums';
import { CondoSpecialsPage } from '../../components/condo/condo-specials';
import { GetawayBookingPage } from '../getaway-booking';
import { AmericanLifestylePage } from '../american-lifestyle';
import { ConciergeTravelTeamPage } from '../concierge';
import { ResourcesPage } from '../resources';
import { CruisesPage } from '../cruises';
import { DailyBenefitsPage } from '../daily-benefits';
import { LuxuryCruisesPage } from '../luxury-cruises';
import { ContactUsPage } from '../contact-us';
import { CertificatesPage } from '../certificates';
import { CertificateCatalogPage } from '../certificates-catalog';
import { CertificateTemplateDetailPage } from '../certificate-template-detail';
import { CertificatePaymentPage } from '../certificate-payment';
import { PublicCertificatePaymentPage } from '../public-certificate-payment';
import { PublicCertificatePaymentSuccessPage } from '../public-certificate-payment-success';
import { CertificatePaymentSuccessPage } from '../certificate-payment-success';
import { CertificateTermsAndConditionsPage } from '../certificate-terms-and-conditions';
import { MasterClassesRegisterPage } from '../master-classes-register';
import { CarsSearchPage } from '../cars-search';
import { CarsDetailPage } from '../cars-details';

import { AllGetawayPage } from '../all-getaway';

import GetawayCondoPage from '../getaway-condo/component';
import AllInclusivesPage from '../all-inclusives/component';
import ReviewBookPage from '../review-book/component';

import { StaticDetailPage } from '../static-details';
import { InsuranceSearchPage } from '../insurance';
import { FlightsPage } from '../flights';

import { LuxuryHomesCheckoutPage } from '../luxury-homes/checkout';
import { LuxuryHomesDetailsPage } from '../luxury-homes/details';
import { LuxuryHomesPlacePage } from '../luxury-homes/place';
import { LuxuryHomesHomePage } from '../luxury-homes/home';
import { LuxuryHomesListPage } from '../luxury-homes/list';
import { getLuxuryHomes } from '@store/slices';

import MLMBecomeTravelAgent from '../mlm/become-travel-agent/component';
import MLMNewAgents from '../mlm/new-agents/component';
import MLMExperiencedAgents from '../mlm/experienced-agents/component';
import MLMExclusiveAccess from '../mlm/exclusive-access/component';
import MLMTraining from '../mlm/training/component';
import MLMTechnology from '../mlm/technology/component';

import { WeeksDetailsPage, WeeksSearchPage } from '../weeks';
import { WeeksReviewBookPage } from '../weeks/weeks-review-book';
import { StripeExpressReturnPage } from '../stripe-express-return';
import { ElitePage } from '../elite';
import { MarketingSpecialsPage } from '../marketing-specials';
import { BlogPage } from '../blog';
import { BlogsPage } from '../blogs/component';
import { CertificateDetailPage } from '../certificate-detail';

export function ApplicationRoutes() {

  const history = useHistory();

  const dispatch = useDispatch();

  const menuStore = useSelector((state: RootState) => state.navigationMenuStore);

  const { items } = menuStore;
  
  const location = useLocation();
  
  const loactionPath = location?.pathname;

  const refresh = () => {
    if (!!items) {
      dispatch(refreshProfile(isHomeFromPath(history) ? IPageType.Home : NULL_VALUE));
      dispatch(refreshAccountTemplate(isAnyResultsFromPath(history)));
    }
  }

  const isLuxuryHomes = isLuxuryHomesFromPath(history);

  useEffect(() => refresh(), [loactionPath]);
  useEffect(() => refresh(), [items?.profile]);
  useEffect(() => getLuxuryHomesData(), [isLuxuryHomes]);

  const getLuxuryHomesData = () => {
    if (isLuxuryHomes) {
      dispatch(getLuxuryHomes());
    }
  }


  return (
    <>
      <PublicClubSiteLandingRedirect />
      <Switch>
      <Route path={`/:accountUsername${Routes.Home}`} exact={true}>
        <HomePage />
      </Route>

      <Route path={`/:accountUsername${Routes.Homes}`}>
        <HomesPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Experiences}`}>
        <ExperiencesPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Cruises}`} exact={true}>
        <CruisesPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Flights}`} exact={true}>
        <FlightsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Cars}`} exact={true}>
        <CarsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Campings}`} exact={true}>
        <CampingsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.CampingsWelcome}`} exact={true}>
        <CampingsWelcomePage />
      </Route>
      <Route path={`/:accountUsername${Routes.CampingsFAQs}`} exact={true}>
        <CampingsFAQsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.CampingsBookingHowTo}`} exact={true}>
        <CampingsBookingHowToPage />
      </Route>
      <Route path={`/:accountUsername${Routes.MarketingSpecials}`} exact={true}>
        <MarketingSpecialsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Cruises}/luxury`}>
        <LuxuryCruisesPage />
      </Route>
      {/*Old Getaways*/}
      <Route path={`/:accountUsername${Routes.Getaway}`} exact={true}>
        <GetawayPage />
      </Route>
      {/*New Getaways*/}
      <Route path={`/:accountUsername${Routes.Getaways}`} exact={true}>
        <AllGetawayPage />
      </Route>
      <Route path={`/:accountUsername${Routes.GetawayDetails}/:id`}>
        <GetawayDetailsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.GetawayCondo}`} exact={true}>
        <GetawayCondoPage isInternal={false} />
      </Route>
      <Route path={`/:accountUsername${Routes.GetawayCondo}/:id`}>
        <GetawayListPage isInternal={false} />
      </Route>

      <Route path={`/:accountUsername${Routes.GetawayCondos}`} exact={true}>
        <GetawayCondoPage isInternal={true} />
      </Route>
      <Route path={`/:accountUsername${Routes.GetawayCondos}/:id`}>
        <GetawayListPage isInternal={true} />
      </Route>

      <Route path={`/:accountUsername${Routes.Getaway}/:id`}>
        <GetawayListPage isInternal={false} />
      </Route>
      <Route path={`/:accountUsername${Routes.GetawayBookingPage}`}>
        <GetawayBookingPage />
      </Route>
      <Route path={`/:accountUsername${Routes.AllInclusive}`} exact>
        <AllInclusivesPage />
      </Route>
      <Route path={`/:accountUsername${Routes.AllInclusive}/submit-request`}>
        <SubmitRequestPage />
      </Route>
      <Route path={`/:accountUsername${Routes.ReservationCancellation}`}>
        <ReservationCancellation />
      </Route>
      <Route path={`/:accountUsername${Routes.RequestCancelReservation}`}>
        <ReservationCancellation />
      </Route>
      <Route path={`/:accountUsername${Routes.AmericanLifestyle}`}>
        <AmericanLifestylePage />
      </Route>
      <Route path={`/:accountUsername${Routes.ConciergeTeam}`}>
        <ConciergeTravelTeamPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Resources}`}>
        <ResourcesPage />
      </Route>
      <Route path={`/:accountUsername${Routes.DailyBenefits}`}>
        <DailyBenefitsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.ContactUs}`}>
        <ContactUsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.PublicCertificatePaymentSuccess}`} exact={true}>
        <PublicCertificatePaymentSuccessPage />
      </Route>
      <Route path={`/:accountUsername${Routes.PublicCertificatePayment}`} exact={true}>
        <PublicCertificatePaymentPage />
      </Route>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.Certificates}`} exact={true}>
        <CertificatesPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.CertificatesCatalog}`}>
        <CertificateCatalogPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.Certificates}/:id`} exact={true}>
        <CertificateDetailPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.CertificateTemplateDetails}/:id`} exact={true}>
        <CertificateTemplateDetailPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.CertificatesTermsAndConditions}/:guid`} exact={true}>
        <CertificateTermsAndConditionsPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.CertificatesPaymentSuccess}`} exact={true}>
        <CertificatePaymentSuccessPage />
      </PrivateCertificateRoute>
      <PrivateCertificateRoute path={`/:accountUsername${Routes.Certificates}/:id/payment`} exact={true}>
        <CertificatePaymentPage />
      </PrivateCertificateRoute>
      <Route path={`/:accountUsername${Routes.MasterClassesRegister}`}>
        <MasterClassesRegisterPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Error}`} exact={true}>
        <ErrorPage />
      </Route>
      <Route path={`/:accountUsername${Routes.MemberStories}/:id`} exact={true}>
        <MemberStoryPage />
      </Route>
      <Route path={`/:accountUsername${Routes.MemberVideos}`} exact={true}>
        <MemberVideosPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Condo}`} exact={true}>
        <Condominiums />
      </Route>
      <Route path={`/:accountUsername${Routes.Condo}/specials`}>
        <CondoSpecialsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.BrioRciHowTo}`}>
        <BrioRciHowToPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Elite}`}>
        <ElitePage />
      </Route>
      <Route path={`/:accountUsername${Routes.NewMembers}`}>
        <NewMembersPage />
      </Route>
      <Route path={`/:accountUsername${Routes.EliteRciHowTo}`}>
        <EliteRciHowToPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Webinars}`}>
        <WebinarsPage />
      </Route>

      <Route path={`/:accountUsername${Routes.PromotionDetails}`}>
        <PromotionDetailsPage />
      </Route>
      <Route path={`/:accountUsername${Routes.PersonalInfo}`} exact={true}>
        <PersonalInfoPage activeKey={UserAccountEnum.PersonalInfo} />
      </Route>
      <Route path={`/:accountUsername${Routes.Security}`} exact={true}>
        <PersonalInfoPage activeKey={UserAccountEnum.Security} />
      </Route>
      <Route path={`/:accountUsername${Routes.PrivacyPolicy}`} exact={true}>
        <Markdown messageId={MarkDownEnum.PrivacyPolicy} />
      </Route>
      <Route path={`/:accountUsername${Routes.PriceMatch}`}>
        <Markdown messageId={MarkDownEnum.PriceMatch} />
      </Route>
      <Route path={`/:accountUsername${Routes.Faq}`}>
        <Markdown messageId={MarkDownEnum.Faq} />
      </Route>

      <PrivateRoute path={`/:accountUsername${Routes.Reservations}`} exact={true}>
        <ReservationsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Rewards}`} exact={true}>
        <RewardsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RewardsLearn}`} exact={true}>
        <RewardsLearnPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RewardsQa}`} exact={true}>
        <RewardsQaPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiAccount}`}>
        <AccountPage key="account" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.AccountReservations}`}>
        <AccountPage key="account_reservation" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiTestDriveManager}`}>
        <AccountPage key="account_test_drive" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiQuotes}/:id`}>
        <AccountPage key="account_my_qoutes" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.SavedProperties}`}>
        <AccountPage key="account_saved_properties" />
      </PrivateRoute>

      <Route path={`/:accountUsername${Routes.Backoffice}`} exact={true}>
        <IframeBackoffice />
      </Route>

      <Route path={`/:accountUsername${Routes.Blog}/:id`}>
        <BlogPage />
      </Route>
      <Route path={`/:accountUsername${Routes.Blogs}`}>
        <BlogsPage />
      </Route>

      <Route path={`/:accountUsername${Routes.TourIframe}/:id`} exact={true}>
        <IframeTours />
      </Route>

      <PrivateRoute path={`/:accountUsername${Routes.CondoSearch}`}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Condo}>
          <CondoPage />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Condo}/:id`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Condo}>
          <CondoDetailPage />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Condo}/:id/:name`} exact={true}>
        <StaticDetailPage key="static-condo" type='condo' />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.UnitBook}/:id`}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Condo}>
          <CondoReviewBookPage />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.CondoSearch}/submit-request`}>
        <SubmitRequestPage isCondoPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.Search}`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Hotel}>
          <SearchPage key="hotels" />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id/${VACATIONS_PAGE}`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Hotel}>
          <HotelDetailPage isVacationRentals={true} key="vacations" />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Hotel}>
          <HotelDetailPage key="hotels" />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id/:name`} exact={true}>
        <StaticDetailPage key="static-hotel" type='hotel' />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Book}/:id/:packageId`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Hotel}>
          <ReviewBookPage key="hotels" />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RequestCancelReservation}/:id`}>
        <ReservationCancellation />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.ReservationCancellation}/:id`}>
        <ReservationCancellation />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.CarsSearch}`}>
        <CarsSearchPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.CarsDetails}/:id`}>
        <CarsDetailPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.Search}/${VACATIONS_PAGE}`} exact={true}>
        <PublicClubSiteSectionGuard itemType={ItemTypeEnum.Hotel}>
          <SearchPage isVacationRentals={true} key="vacations" />
        </PublicClubSiteSectionGuard>
      </PrivateRoute>
      <PrivateRoute
        path={`/:accountUsername${Routes.Book}/:id/:packageId/${VACATIONS_PAGE}`}
        exact={true}
      >
        <ReviewBookPage isVacationRentals={true} key="vacations" />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.StripeBookHotel}/:id/:packageId`} exact={true}>
        <ReviewBookPage isStripe key="stripe-hotels" />
      </PrivateRoute>
      <PrivateRoute
        path={`/:accountUsername${Routes.StripeBookHotel}/:id/:packageId/${VACATIONS_PAGE}`}
        exact={true}
      >
        <ReviewBookPage isVacationRentals={true} isStripe key="stripe-vacations" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeBookCondo}/:id`}>
        <CondoReviewBookPage isStripe />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeBookCar}/:id`}>
        <CarsDetailPage isStripe />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.SidekickRewards}`} exact={true}>
        <SidekickPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.SidekickRewardsTerms}`} exact={true}>
        <SidekickTermsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.SidekickRewardsPrivacy}`} exact={true}>
        <SidekickPrivacyPage />
      </PrivateRoute>

      <Route path={`/:accountUsername${Routes.ThreeDS}`} exact={true}>
        <ThreeDS />
      </Route>

      <PrivateRoute path={`/:accountUsername${Routes.StripeReturnHotel}`} exact={true}>
        <StripeExpressReturnPage vertical="hotel" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeReturnCondo}`} exact={true}>
        <StripeExpressReturnPage vertical="condo" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeReturnWeeks}`} exact={true}>
        <StripeExpressReturnPage vertical="weeks" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeReturnCars}`} exact={true}>
        <StripeExpressReturnPage vertical="cars" />
      </PrivateRoute>

      <Route path={`/:accountUsername${Routes.Login}`} exact={true}>
        <SignIn />
      </Route>
      <Route path={`/:accountUsername${Routes.Login}/:bookingID`} exact={true}>
        <SignIn />
      </Route>
      <Route path={`/:accountUsername${Routes.SignUp}`} exact={true}>
        <SignUp />
      </Route>
      <Route path={`/:accountUsername${Routes.SignUp}/:bookingID`} exact={true}>
        <SignUp />
      </Route>

      <Route path={`/:accountUsername${Routes.VerifyUser}/:token`} exact={true}>
        <VerifyUser />
      </Route>

      <Route path={`/:accountUsername${Routes.SendVerifyEmail}`} exact={true}>
        <SendVerification />
      </Route>
      <Route path={`/:accountUsername${Routes.ForgotPassword}`} exact={true}>
        <ForgotPassword />
      </Route>
      <Route path={`/:accountUsername${Routes.ResetPassword}/:token`} exact={true}>
        <ResetPassword />
      </Route>
      <Route path={`/:accountUsername${Routes.ChangePassword}`} exact={true}>
        <ChangePassword />
      </Route>
      <Route path={`/:accountUsername${Routes.SavedPropertiesAccount}`} exact={true}>
        <SavedProperty />
      </Route>

      <Route path={`${Routes.CondosMap}`} exact={true}>
        <CondoWorldMap />
      </Route>
      <Route path={`${Routes.CondosWeeks}`} exact={true}>
        <CondoWeeksWrapper />
      </Route>
      <Route path={`/:accountUsername${Routes.CondosWeeks}`} exact={true}>
        <CondoWeeksWrapper isAccount />
      </Route>
      <Route path={`/:accountUsername${Routes.CondosWeeklySpecials}`} exact={true}>
        <CondoWeeksWrapper isAccount isWeeklySpecials />
      </Route>
      <Route path={`/:accountUsername${Routes.CondosWeeklySpecials}/marketing`} exact={true}>
        <CondoWeeksWrapper isAccount isWeeklySpecials isMarketing />
      </Route>
      <Route path={`/:accountUsername${Routes.CondosWeeklySpecials}/:id`} exact={true}>
        <CondoWeeksWrapper isAccount isWeeklySpecials isWeeklySpecialsParam />
      </Route>

      <PrivateRoute path={`/:accountUsername${Routes.WeeksSearch}`}>
        <WeeksSearchPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.WeeksDetails}/:id`}>
        <WeeksDetailsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.WeeksBook}/:id/:packageId`}>
        <WeeksReviewBookPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.StripeBookWeek}/:id/:packageId`}>
        <WeeksReviewBookPage isStripe />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.MLMBecomeAgent}`}>
        <MLMBecomeTravelAgent />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.MLMNewAgents}`}>
        <MLMNewAgents />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.MLMExperiencedAgents}`}>
        <MLMExperiencedAgents />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.MLMExclusiveAccess}`}>
        <MLMExclusiveAccess />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.MLMTraining}`}>
        <MLMTraining />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.MLMTechnology}`}>
        <MLMTechnology />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Dashboard}`}>
        <AccountHubDashboardGate />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.InsuranceSearch}`}>
        <InsuranceSearchPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomes}`} exact>
        <LuxuryHomesHomePage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesList}`} exact>
        <LuxuryHomesListPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesDetails}/:propertyId/:roomId`} exact>
        <LuxuryHomesDetailsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesDetails}/:placeId/:propertyId/:roomId`} exact>
        <LuxuryHomesDetailsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesDetails}/:listId/:placeId/:propertyId/:roomId`} exact>
        <LuxuryHomesDetailsPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesCheckout}/:propertyId/:roomId/:packageId`} exact>
        <LuxuryHomesCheckoutPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesCheckout}/:placeId/:propertyId/:roomId/:packageId`} exact>
        <LuxuryHomesCheckoutPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesPlace}/:placeId`} exact>
        <LuxuryHomesPlacePage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.LuxuryHomesPlace}/:placeId/:propertyId`} exact>
        <LuxuryHomesPlacePage />
      </PrivateRoute>

      <Route path={Routes.Error}>
        <ErrorPanel />
      </Route>

      <Route path="*">
        <GenericNotFoundPage />
      </Route>
    </Switch>
    </>
  );
}
